import { makeSelectBundlePageIdByPageIndex, RootState, setViewportPageIdForRestorePageIndexInStore } from '@/store';
import { api } from '../core';
import { BundleSectionsTreeNode, refreshBundleSectionsWithCurrentFilters } from './getBundleSections';

export const ReorderBundlePagesPasteLocationType = {
  BEFORE: 'before',
  AFTER: 'after',
} as const;

export type ReorderBundlePagesPasteLocationTypeKeys =
  (typeof ReorderBundlePagesPasteLocationType)[keyof typeof ReorderBundlePagesPasteLocationType];

interface ReorderBundlePagesResponse {
  pageIds: string[];
  sidebar: BundleSectionsTreeNode[];
}

interface ReorderBundlePagesProps {
  bundleId: string;
  targetParentId: string;
  anchorPageId: string;
  /**
   * Position of anchorPageId within targetParentId. Sent alongside anchorPageId
   * only until the backend reads the anchor, and can be dropped once it does.
   */
  targetIndex: number;
  pageIds: string[];
  pasteLocation: ReorderBundlePagesPasteLocationTypeKeys;
}

interface LegacyReorderBundlePagesProps {
  bundleId: string;
  targetParentId: string;
  targetIndex: number;
  pageIds: string[];
  pasteLocation: ReorderBundlePagesPasteLocationTypeKeys;
}

type ReorderDispatch = Parameters<typeof refreshBundleSectionsWithCurrentFilters>[0];

const restoreViewportAndRefreshSections = async (
  queryFulfilled: Promise<{ data: ReorderBundlePagesResponse }>,
  dispatch: ReorderDispatch,
  getState: () => unknown,
) => {
  try {
    const { data } = await queryFulfilled;
    const state = getState() as RootState;

    const getBundlePageIdByPageIndex = makeSelectBundlePageIdByPageIndex(state);
    const currentPageId = getBundlePageIdByPageIndex(state.bundleViewportData.viewportPageIndexInStore);

    if (data && state.sharedData.activeBundleType) {
      dispatch(setViewportPageIdForRestorePageIndexInStore(currentPageId));
      refreshBundleSectionsWithCurrentFilters(dispatch, state);
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('Request was aborted');
    } else {
      console.error('Error in reorderBundlePages in onQueryStarted:', error);
    }
  }
};

const reorderBundlePagesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    reorderBundlePages: builder.mutation<ReorderBundlePagesResponse, ReorderBundlePagesProps>({
      query: ({
        bundleId,
        targetParentId,
        anchorPageId,
        targetIndex,
        pageIds,
        pasteLocation,
      }: ReorderBundlePagesProps) => ({
        url: 'bundle/reorder',
        method: 'POST',
        body: {
          bundleId,
          targetParentId,
          anchorPageId,
          targetIndex,
          pageIds,
          pasteLocation,
        },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        await restoreViewportAndRefreshSections(queryFulfilled, dispatch, getState);
      },
      extraOptions: { maxRetries: 0 },
    }),
    legacyReorderBundlePages: builder.query<ReorderBundlePagesResponse, LegacyReorderBundlePagesProps>({
      query: ({ bundleId, targetParentId, targetIndex, pageIds, pasteLocation }: LegacyReorderBundlePagesProps) => ({
        url: 'bundle/reorder',
        method: 'POST',
        body: {
          bundleId,
          targetParentId,
          targetIndex,
          pageIds,
          pasteLocation,
        },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        await restoreViewportAndRefreshSections(queryFulfilled, dispatch, getState);
      },
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useReorderBundlePagesMutation, useLazyLegacyReorderBundlePagesQuery } = reorderBundlePagesApi;
